import { array, ord, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { z } from 'zod';
import { createZodFormResolver, LocalizedText, showSuccessAlert, useFormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Enterprise, useRepositoryMutation } from '@mews-commander/data-access';
import { useInvalidateUserProfile } from '../../../hooks/useInvalidateUserProfile';
import { useUserProfilesGet } from '../../../queries';

const schema = z.object({
    enterpriseIds: z.array(z.string()).min(1),
    sourceEnterpriseId: z.string().optional(),
});

type AssignPropertiesDialogFormValues = z.infer<typeof schema>;

const resolver = createZodFormResolver(schema);

export const useAssignProperties = ({ userId, onSuccess }: { userId: string; onSuccess: () => void }) => {
    const invalidateUserProfile = useInvalidateUserProfile();
    const { data: detailedUser, isLoading: isLoadingDetailedUser } = useUserProfilesGet(userId);
    const fullName = detailedUser?.fullName || '';

    const assignProperties = useRepositoryMutation('userProfiles', 'updateProperties', {
        onSuccess: () => {
            invalidateUserProfile(userId);

            showSuccessAlert({
                description: (
                    <LocalizedText
                        textKey={TextKey.UserProfileAssignPropertiesSuccessMessage}
                        substitutes={{ user: fullName }}
                    />
                ),
            });
        },
    });

    const orderByName: ord.Ord<Enterprise> = pipe(
        string.Ord,
        ord.contramap(({ name }) => name.toLowerCase())
    );

    const sortedEnterprises = array.sortBy([orderByName])(detailedUser?.enterprises || []);

    const otherMethods = useFormControls<AssignPropertiesDialogFormValues>({
        onSubmit: ({ enterpriseIds, sourceEnterpriseId }) => {
            assignProperties.mutate(
                {
                    enterpriseIds,
                    sourceEnterpriseId,
                    userId,
                },
                { onSuccess }
            );
        },
        resolver,
        defaultValues: {
            enterpriseIds: [],
            sourceEnterpriseId: detailedUser?.overrideComparator?.allEqual ? undefined : sortedEnterprises[0]?.id,
        },
    });

    return {
        ...otherMethods,
        detailedUser,
        isLoading: assignProperties.isPending || isLoadingDetailedUser,
    };
};
